import { FastifyPluginAsync } from 'fastify'
import { createReadStream, existsSync } from 'fs'
import { join } from 'path'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('tiles.routes')

/**
 * Tiles Routes - Authenticated Vector Tiles Proxy
 * 
 * Serves vector tiles from the tiles directory with authentication.
 * Only authenticated users can access tiles.
 */
const tilesRoutes: FastifyPluginAsync = async (fastify) => {
    // Base path for tiles (adjust based on deployment)
    const TILES_BASE_PATH = process.env.TILES_PATH || '/srv/myroadtrip/tiles/pois'

    /**
     * GET /api/tiles/:z/:x/:y.pbf
     * Serve vector tiles with authentication
     */
    fastify.get(
        '/:z/:x/:y.pbf',
        {
            schema: {
                params: {
                    type: 'object',
                    required: ['z', 'x', 'y'],
                    properties: {
                        z: { type: 'string', pattern: '^[0-9]+$' },
                        x: { type: 'string', pattern: '^[0-9]+$' },
                        y: { type: 'string', pattern: '^[0-9]+$' }
                    }
                }
            }
        },
        async (request, reply) => {
            const { z, x, y } = request.params as { z: string; x: string; y: string }
            const userId = request.user?.sub

            // Validate zoom level (0-14 for our tiles)
            const zoomLevel = parseInt(z, 10)
            if (zoomLevel < 0 || zoomLevel > 14) {
                return reply.code(400).send({ error: 'Invalid zoom level' })
            }

            // Construct tile path
            const tilePath = join(TILES_BASE_PATH, z, x, `${y}.pbf`)

            // Check if tile exists
            if (!existsSync(tilePath)) {
                logger.debug(`Tile not found: ${tilePath}`)
                return reply.code(404).send({ error: 'Tile not found' })
            }

            // Log tile access (optional, for analytics)
            logger.debug(`Tile accessed by user ${userId}: ${z}/${x}/${y}.pbf`)

            // Set headers for vector tiles
            reply.headers({
                'Content-Type': 'application/x-protobuf',
                'Content-Encoding': 'gzip',
                'Cache-Control': 'public, max-age=86400', // Cache for 24 hours
                'Access-Control-Allow-Origin': '*', // Allow CORS for map tiles
                'Access-Control-Allow-Methods': 'GET, OPTIONS',
                'Access-Control-Allow-Headers': 'Content-Type, Authorization'
            })

            // Stream the tile file
            return reply.send(createReadStream(tilePath))
        }
    )

    /**
     * GET /api/tiles/:region/:z/:x/:y.pbf
     * Serve region-specific vector tiles with authentication
     */
    fastify.get(
        '/:region/:z/:x/:y.pbf',
        {
            schema: {
                params: {
                    type: 'object',
                    required: ['region', 'z', 'x', 'y'],
                    properties: {
                        region: { type: 'string' },
                        z: { type: 'string', pattern: '^[0-9]+$' },
                        x: { type: 'string', pattern: '^[0-9]+$' },
                        y: { type: 'string', pattern: '^[0-9]+$' }
                    }
                }
            }
        },
        async (request, reply) => {
            const { region, z, x, y } = request.params as {
                region: string
                z: string
                x: string
                y: string
            }
            const userId = request.user?.sub

            // Validate zoom level
            const zoomLevel = parseInt(z, 10)
            if (zoomLevel < 0 || zoomLevel > 14) {
                return reply.code(400).send({ error: 'Invalid zoom level' })
            }

            // Construct region-specific tile path
            const tilePath = join(TILES_BASE_PATH, region, z, x, `${y}.pbf`)

            // Check if tile exists
            if (!existsSync(tilePath)) {
                logger.debug(`Region tile not found: ${tilePath}`)
                return reply.code(404).send({ error: 'Tile not found' })
            }

            // Log tile access
            logger.debug(`Region tile accessed by user ${userId}: ${region}/${z}/${x}/${y}.pbf`)

            // Set headers
            reply.headers({
                'Content-Type': 'application/x-protobuf',
                'Content-Encoding': 'gzip',
                'Cache-Control': 'public, max-age=86400',
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'GET, OPTIONS',
                'Access-Control-Allow-Headers': 'Content-Type, Authorization'
            })

            // Stream the tile file
            return reply.send(createReadStream(tilePath))
        }
    )
}

export default tilesRoutes
